/**
 * Move-in Date Filter Service
 *
 * Matches a housing unit's `moveInDate` (ISO 8601 UTC string, NHS20-9124) against the Filters
 * modal's Move-in date selection. Each `MoveInDateRange` instance is a cumulative "on or before N"
 * window from now, not a bucket — `NinetyDays` includes everything `ThirtyDays` would, per the
 * enum's own doc comments. A unit with no `moveInDate` never matches an active filter, the same
 * no-value-means-no-match rule `rangeFilterService.js#matchesRange` uses. House plans don't carry
 * `moveInDate` at all — this only ever applies to housing units.
 *
 * @module nativeInteractiveSiteMap/services/moveInDateFilterService
 */

import MoveInDateRange from 'enums/moveInDateRange';

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date, months) {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function cutoffFor(moveInDateRange, now) {
  switch (moveInDateRange) {
    case MoveInDateRange.ThirtyDays:
      return addDays(now, 30);
    case MoveInDateRange.NinetyDays:
      return addDays(now, 90);
    case MoveInDateRange.SixMonths:
      return addMonths(now, 6);
    default:
      return null;
  }
}

/**
 * @param {string|null|undefined} moveInDateIso - The unit's `moveInDate`, or null/undefined when unknown.
 * @param {string|null} moveInDateRangeKey - A `MoveInDateRange` key (e.g. `'30-days'`), or `null` for no filter.
 * @param {Date} [now] - Injectable clock for tests; defaults to the real current time.
 * @returns {boolean}
 */
export default function matchesMoveInDate(moveInDateIso, moveInDateRangeKey, now = new Date()) {
  if (moveInDateRangeKey == null) {
    return true;
  }

  if (moveInDateIso == null) {
    return false;
  }

  // An unrecognized key resolves to no MoveInDateRange instance and falls through cutoffFor's
  // `default`, so it matches nothing — the null/undefined check above is the only case treated as
  // "no filter"; an unrecognized key is not.
  const cutoff = cutoffFor(MoveInDateRange.fromKey(moveInDateRangeKey), now);
  return cutoff != null && new Date(moveInDateIso) <= cutoff;
}
